import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Pagination,
  Menu,
  AppBar,
  Toolbar,
  Slide,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search, X, Building2, Pencil, CheckCircle2, XCircle, Clock, Filter } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import api from '../services/api';

const last5 = (val) => (val ? String(val).slice(-5) : '');
const money = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const FullScreenDialogTransition = React.forwardRef(function FullScreenDialogTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// tenta “entender” vários formatos possíveis de before_json/after_json
function normalizeSaleJson(payload) {
  if (!payload || typeof payload !== 'object') return null;

  // formato: { sale: {...}, items: [...] }
  const sale = payload.sale && typeof payload.sale === 'object' ? payload.sale : payload;

  const itemsRaw = Array.isArray(payload.items) ? payload.items : Array.isArray(sale.items) ? sale.items : [];

  const items = itemsRaw.map((it) => {
    const name = it.name ?? it.service_name ?? it.abbreviation ?? 'Item';
    const qty = Number(it.quantity ?? it.qty ?? 0) || 0;
    const unit_price = Number(it.unit_price ?? it.price ?? 0) || 0;

    return {
      name,
      qty,
      unit_price,
      subtotal: qty * unit_price,
      service_id: it.service_id ?? null,
    };
  });

  const payment_method = sale.payment_method ?? '';
  const tip = Number(sale.tip ?? 0) || 0;
  const net_no_tip = Number(sale.subtotal ?? 0) || 0;
  const total_amount = Number(sale.total_amount ?? net_no_tip + tip) || 0;

  return {
    payment_method,
    tip,
    total_amount,
    net_no_tip,
    time_local: sale.time_local ?? '',
    employee_name: sale.employee_name ?? '',
    items,
  };
}

function buildItemKey(it) {
  // chave simples: nome + unit_price
  return `${String(it.name || '').toLowerCase()}|${Number(it.unit_price || 0).toFixed(2)}`;
}

function computeDiff(beforeNorm, afterNorm) {
  const diff = {
    payment_method: false,
    tip: false,
    total_amount: false,
    net_no_tip: false,
    itemsChangedKeys: new Set(),
    itemsAddedKeys: new Set(),
    itemsRemovedKeys: new Set(),
  };

  if (!beforeNorm || !afterNorm) return diff;

  diff.payment_method = (beforeNorm.payment_method || '') !== (afterNorm.payment_method || '');
  diff.tip = Number(beforeNorm.tip || 0) !== Number(afterNorm.tip || 0);
  diff.total_amount = Number(beforeNorm.total_amount || 0) !== Number(afterNorm.total_amount || 0);
  diff.net_no_tip = Number(beforeNorm.net_no_tip || 0) !== Number(afterNorm.net_no_tip || 0);

  const bMap = new Map();
  const aMap = new Map();

  (beforeNorm.items || []).forEach((it) => bMap.set(buildItemKey(it), it));
  (afterNorm.items || []).forEach((it) => aMap.set(buildItemKey(it), it));

  for (const [k, b] of bMap.entries()) {
    if (!aMap.has(k)) {
      diff.itemsRemovedKeys.add(k);
      continue;
    }
    const a = aMap.get(k);
    const qtyChanged = Number(b.qty || 0) !== Number(a.qty || 0);
    const subtotalChanged = Number(b.subtotal || 0) !== Number(a.subtotal || 0);
    if (qtyChanged || subtotalChanged) diff.itemsChangedKeys.add(k);
  }

  for (const [k] of aMap.entries()) {
    if (!bMap.has(k)) diff.itemsAddedKeys.add(k);
  }

  return diff;
}

function Highlight({ active, children }) {
  if (!active) return children;
  return (
    <Box component="span" sx={{ px: 0.5, borderRadius: 1, bgcolor: 'rgba(255, 235, 59, 0.55)' }}>
      {children}
    </Box>
  );
}

function ReceiptCard({ title, tone = 'before', meta, saleNorm, diff, rawFallback }) {
  const isBefore = tone === 'before';

  const items = saleNorm?.items || [];
  const mapKey = (it) => buildItemKey(it);

  const lineHighlight = (it) => {
    const k = mapKey(it);
    if (isBefore) {
      if (diff.itemsRemovedKeys.has(k)) return true; // existed, removed later
      if (diff.itemsChangedKeys.has(k)) return true; // qty/subtotal changed
      return false;
    }
    // after
    if (diff.itemsAddedKeys.has(k)) return true;
    if (diff.itemsChangedKeys.has(k)) return true;
    return false;
  };

  const headerColor = title === 'AFTER' ? 'success.main' : 'text.primary';

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: title === 'AFTER' ? 'success.200' : 'grey.300',
        bgcolor: title === 'AFTER' ? 'success.50' : 'grey.50',
        fontFamily: 'monospace',
      }}
      elevation={0}
      variant="outlined"
    >
      <Box sx={{ textAlign: 'center', mb: 1, borderBottom: '1px dashed', borderColor: 'grey.400', pb: 1 }}>
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, color: headerColor }}>{title}</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {meta}
        </Typography>
      </Box>

      {!saleNorm ? (
        <Box>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            Could not parse receipt format. Showing raw JSON:
          </Typography>
          <Paper sx={{ mt: 1, p: 1, bgcolor: '#fff', borderRadius: 1 }} variant="outlined">
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(rawFallback || {}, null, 2)}</pre>
          </Paper>
        </Box>
      ) : (
        <Box>
          {/* top fields */}
          <Box sx={{ mb: 1 }}>
            {saleNorm.employee_name ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                Employee: {saleNorm.employee_name}
              </Typography>
            ) : null}
            {saleNorm.time_local ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                Time: {saleNorm.time_local}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
              Payment:{' '}
              <Highlight active={diff.payment_method}>
                <strong>{saleNorm.payment_method || '—'}</strong>
              </Highlight>
            </Typography>
          </Box>

          <Box sx={{ borderBottom: '1px dashed', borderColor: 'grey.400', mb: 1 }} />

          {/* items */}
          <Box sx={{ mb: 1 }}>
            {items.length === 0 ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                (No items)
              </Typography>
            ) : (
              items.map((it, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ flex: 1, pr: 1 }}>
                    <Highlight active={lineHighlight(it)}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700 }}>
                        {it.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace', fontSize: '0.70rem', color: 'text.secondary' }}
                      >
                        {Number(it.qty || 0)}x {money(it.unit_price)}
                      </Typography>
                    </Highlight>
                  </Box>
                  <Highlight active={lineHighlight(it)}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700 }}>
                      {money(it.subtotal)}
                    </Typography>
                  </Highlight>
                </Box>
              ))
            )}
          </Box>

          <Box sx={{ borderBottom: '1px dashed', borderColor: 'grey.400', mb: 1 }} />

          {/* totals */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                SUBTOTAL:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 800 }}>
                <Highlight active={diff.net_no_tip}>{money(saleNorm.net_no_tip)}</Highlight>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                TIP:
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 800 }}>
                <Highlight active={diff.tip}>{money(saleNorm.tip)}</Highlight>
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 0.5,
                borderTop: '1px solid',
                borderColor: 'grey.400',
              }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.86rem', fontWeight: 900 }}>
                TOTAL:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.86rem',
                  fontWeight: 900,
                  color: title === 'AFTER' ? 'success.main' : 'primary.main',
                }}
              >
                <Highlight active={diff.total_amount}>{money(saleNorm.total_amount)}</Highlight>
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export default function EditPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [hotels, setHotels] = useState([{ id: '', name: 'All Hotels' }]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const searchTimeoutRef = useRef(null);

  const [filters, setFilters] = useState({
    hotel_id: '',
    status: '',
    edit_type: '',
    start_date: null,
    end_date: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // ✅ review UI state
  const [reviewReason, setReviewReason] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // ✅ filters UI (match SalesPage top)
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const res = await api.get('/admin/hotels');
        setHotels([{ id: '', name: 'All Hotels' }, ...(res.data || [])]);
      } catch (e) {
        console.error(e);
        showSnackbar('Error loading hotels', 'error');
      }
    };
    fetchHotels();
  }, []);

  const fetchData = useCallback(async (currentPage, currentSearch, currentFilters, showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const params = {
        page: currentPage + 1,
        per_page: 50,
        search: (currentSearch || '').trim(),
      };

      if (currentFilters.hotel_id) params.hotel_id = currentFilters.hotel_id;
      if (currentFilters.status) params.status = currentFilters.status;
      if (currentFilters.edit_type) params.edit_type = currentFilters.edit_type;

      if (currentFilters.start_date) params.start_date = currentFilters.start_date;
      if (currentFilters.end_date) params.end_date = currentFilters.end_date;

      const res = await api.get('/admin/sale-edit-requests', { params });

      const data = res.data?.edit_requests || [];
      setRows(data);

      const p = res.data?.pagination;
      const tr = p?.total_rows ?? res.data?.count ?? 0;
      setTotalRows(tr);
      setTotalPages(p?.total_pages ?? Math.ceil(tr / 50));
    } catch (e) {
      console.error(e);
      showSnackbar('Error loading sale edit requests', 'error');
      setRows([]);
      setTotalRows(0);
      setTotalPages(0);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(page, search, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchData]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      fetchData(0, search, filters);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setPage(0);
    fetchData(0, search, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const clearFilters = () => {
    setSearch('');
    setFilters({ hotel_id: '', status: '', edit_type: '', start_date: null, end_date: null });
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const handleChangePage = (_event, newPage) => setPage(newPage - 1);

  const openDetails = (row) => {
    setSelected(row);
    setReviewReason(row?.review_reason || '');
    setDialogOpen(true);
  };

  const closeDetails = () => {
    if (reviewSubmitting) return;
    setDialogOpen(false);
    setTimeout(() => {
      setSelected(null);
      setReviewReason('');
    }, 150);
  };

  const statusChip = (s) => {
    const val = (s || '').toUpperCase();
    if (val.includes('APPROV')) return <Chip size="small" color="success" label={val} icon={<CheckCircle2 size={16} />} />;
    if (val.includes('REJECT')) return <Chip size="small" color="error" label={val} icon={<XCircle size={16} />} />;
    if (val.includes('PEND')) return <Chip size="small" color="warning" label={val} icon={<Clock size={16} />} />;
    return <Chip size="small" variant="outlined" label={val || '—'} />;
  };

  const formattedId = (saleId, localId) => {
    if (!saleId && !localId) return '—';
    if (localId) return `Sale ${saleId} | Local ${last5(localId)}`;
    return `Sale ${saleId}`;
  };

  const beforeNorm = useMemo(() => normalizeSaleJson(selected?.before_json), [selected]);
  const afterNorm = useMemo(() => normalizeSaleJson(selected?.after_json), [selected]);
  const diff = useMemo(() => computeDiff(beforeNorm, afterNorm), [beforeNorm, afterNorm]);

  const canReview = (selected?.status || '').toUpperCase() === 'PENDING';

  const applyReviewLocalState = (statusUpper, reasonText) => {
    const nowIso = new Date().toISOString();

    setSelected((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        status: statusUpper,
        review_reason: reasonText || prev.review_reason,
        reviewed_at: prev.reviewed_at || nowIso,
      };
    });

    setRows((prev) =>
      (prev || []).map((r) => {
        if (r.id !== selected?.id) return r;
        return {
          ...r,
          status: statusUpper,
          review_reason: reasonText || r.review_reason,
          reviewed_at: r.reviewed_at || nowIso,
        };
      })
    );
  };

  const handleSubmitReview = async (statusUpper) => {
    if (!selected?.id) return;

    const status = String(statusUpper || '').toUpperCase();
    const reasonTrim = (reviewReason || '').trim();

    if (status === 'REJECTED' && !reasonTrim) {
      showSnackbar('Review reason is required when rejecting.', 'error');
      return;
    }

    setReviewSubmitting(true);
    try {
      await api.post(`/admin/sale-edit-requests/${selected.id}/review`, {
        status,
        review_reason: status === 'REJECTED' ? reasonTrim : reasonTrim || '',
      });

      applyReviewLocalState(status, reasonTrim);

      showSnackbar(status === 'APPROVED' ? 'Request approved.' : 'Request rejected.', 'success');
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.data?.description ||
        'Error submitting review';
      showSnackbar(msg, 'error');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const hasActiveFilters = Boolean(
    (search && search.trim()) ||
      filters.hotel_id ||
      filters.edit_type ||
      filters.start_date ||
      filters.end_date ||
      (filters.status && filters.status !== '')
  );

  const openFilters = (e) => {
    if (isMobile) {
      setFilterDialogOpen(true);
    } else {
      setFilterMenuAnchor(e.currentTarget);
    }
  };

  const closeFilters = () => {
    setFilterMenuAnchor(null);
    setFilterDialogOpen(false);
  };

  const FiltersContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <FormControl size="small" fullWidth>
        <InputLabel>Hotel</InputLabel>
        <Select
          value={filters.hotel_id}
          label="Hotel"
          onChange={(e) => handleFilterChange('hotel_id', e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <Building2 size={18} />
            </InputAdornment>
          }
        >
          {hotels.map((h) => (
            <MenuItem key={h.id || 'all'} value={h.id}>
              {h.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Status</InputLabel>
          <Select value={filters.status} label="Status" onChange={(e) => handleFilterChange('status', e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">PENDING</MenuItem>
            <MenuItem value="APPROVED">APPROVED</MenuItem>
            <MenuItem value="REJECTED">REJECTED</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>Edit Type</InputLabel>
          <Select
            value={filters.edit_type}
            label="Edit Type"
            onChange={(e) => handleFilterChange('edit_type', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ITEMS">ITEMS</MenuItem>
            <MenuItem value="PAYMENT">PAYMENT</MenuItem>
            <MenuItem value="TIP">TIP</MenuItem>
            <MenuItem value="TOTAL">TOTAL</MenuItem>
            <MenuItem value="OTHER">OTHER</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          fullWidth
          value={filters.start_date || ''}
          onChange={(e) => handleFilterChange('start_date', e.target.value || null)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          fullWidth
          value={filters.end_date || ''}
          onChange={(e) => handleFilterChange('end_date', e.target.value || null)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Box>
  );

  const SearchBar = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        p: 0,
        overflow: 'hidden',
        border: '2px solid transparent',
        backgroundImage: `
          linear-gradient(#fff, #fff),
          linear-gradient(180deg, #ec4899, #a855f7)
        `,
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.92)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch" justifyContent="space-between">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems="stretch" flex={1}>
            <TextField
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sale/local ID, hotel, employee, editor..."
              size="small"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearch('')} size="small" edge="end" sx={{ mr: -0.5 }}>
                      <X size={16} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: 'flex-end' }}
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                alignItems: 'center',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'auto auto' },
                justifyContent: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              <Button
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                onClick={openFilters}
                startIcon={<Filter size={16} />}
                size="small"
                sx={{
                  height: 40,
                  fontWeight: 900,
                  borderRadius: 2,
                  width: { xs: '100%', md: 'auto' },
                  minWidth: { md: 110 },
                  whiteSpace: 'nowrap',
                }}
              >
                Filters
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={clearFilters}
                  startIcon={<X size={16} />}
                  size="small"
                  sx={{
                    width: { xs: '100%', md: 'auto' },
                    height: 40,
                    fontWeight: 900,
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Stack>
        </Stack>

        {/* DESKTOP: Menu */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={closeFilters}
          PaperProps={{ sx: { p: 2, borderRadius: 2, width: 420, maxWidth: 460 } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography fontWeight={900}>Advanced Filters</Typography>
            <IconButton onClick={closeFilters} size="small">
              <X size={16} />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {FiltersContent}

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="text" color="inherit" onClick={clearFilters} startIcon={<X size={16} />} sx={{ fontWeight: 800 }}>
              Clear
            </Button>
            <Button variant="contained" onClick={closeFilters} sx={{ fontWeight: 900 }}>
              Done
            </Button>
          </Stack>
        </Menu>

        {/* MOBILE: Fullscreen Dialog */}
        <Dialog fullScreen open={filterDialogOpen} onClose={closeFilters} TransitionComponent={FullScreenDialogTransition}>
          <AppBar sx={{ position: 'relative' }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Filter size={18} />
                <Typography fontWeight={900}>Filters</Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {hasActiveFilters && (
                  <Button color="inherit" onClick={clearFilters} sx={{ fontWeight: 900 }}>
                    Clear
                  </Button>
                )}
                <Button color="inherit" onClick={closeFilters} sx={{ fontWeight: 900 }}>
                  Close
                </Button>
              </Stack>
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 2 }}>
            <Typography fontWeight={900} sx={{ mb: 1 }}>
              Advanced Filters
            </Typography>

            {FiltersContent}

            <Box sx={{ mt: 2 }}>
              <Button fullWidth variant="contained" onClick={closeFilters} sx={{ height: 44, borderRadius: 2, fontWeight: 900 }}>
                Apply
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {SearchBar}

        {/* Table (unchanged) */}
        <Box
          sx={{
            mt: 2,
            overflowX: 'auto',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            width: '100%',
          }}
        >
          <Paper sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: '#f0f0f0', fontWeight: 'bold', whiteSpace: 'nowrap' } }}>
                    <TableCell>Sale</TableCell>
                    <TableCell>Hotel</TableCell>
                    <TableCell>Original Employee</TableCell>
                    <TableCell>Edited By</TableCell>
                    <TableCell>Edit Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Reviewed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : !loading && rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No edit requests found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow
                        hover
                        key={r.id}
                        onClick={() => openDetails(r)}
                        sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', cursor: 'pointer' } }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Chip size="small" variant="outlined" color="primary" label={formattedId(r.sale_id, r.local_sale_id)} />
                        </TableCell>
                        <TableCell>{r.hotel_name || (r.hotel_id ? `Hotel ${r.hotel_id}` : '—')}</TableCell>
                        <TableCell>{r.original_employee_name || '—'}</TableCell>
                        <TableCell>{r.edited_by_employee_name || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" icon={<Pencil size={16} />} label={r.edit_type || '—'} variant="outlined" />
                        </TableCell>
                        <TableCell>{statusChip(r.status)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {r.created_at ? format(parseISO(r.created_at), 'MM/dd/yy HH:mm') : '—'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {r.reviewed_at ? format(parseISO(r.reviewed_at), 'MM/dd/yy HH:mm') : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Pagination */}
        {!loading && totalRows > 0 && (
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" mt={2} px={1} mx="auto">
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {`Records ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalRows)} of ${totalRows}`}
            </Typography>
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={handleChangePage}
              color="primary"
              shape="rounded"
              size={isMobile ? 'small' : 'medium'}
            />
          </Stack>
        )}

        {/* Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={closeDetails}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 'bold',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Pencil size={22} />
              <span>Sale Edit Request — {formattedId(selected?.sale_id, selected?.local_sale_id)}</span>
            </Box>
            <IconButton onClick={closeDetails} size="small" disabled={reviewSubmitting}>
              <X size={18} />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {!selected ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Meta */}
                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Hotel
                      </Typography>
                      <Typography fontWeight="bold">{selected.hotel_name || `Hotel ${selected.hotel_id || '—'}`}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Edited By
                      </Typography>
                      <Typography fontWeight="bold">{selected.edited_by_employee_name || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Original Employee
                      </Typography>
                      <Typography fontWeight="bold">{selected.original_employee_name || '—'}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Edit Type
                      </Typography>
                      <Chip size="small" label={selected.edit_type || '—'} variant="outlined" />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      {statusChip(selected.status)}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Created
                      </Typography>
                      <Typography fontWeight="bold">
                        {selected.created_at ? format(parseISO(selected.created_at), 'MM/dd/yyyy HH:mm:ss') : '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed By
                      </Typography>
                      <Typography fontWeight="bold">{selected.reviewed_by_employee_name || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed At
                      </Typography>
                      <Typography fontWeight="bold">
                        {selected.reviewed_at ? format(parseISO(selected.reviewed_at), 'MM/dd/yyyy HH:mm:ss') : '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Review Reason
                      </Typography>
                      <Typography fontWeight="bold">{selected.review_reason || '—'}</Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* ✅ Approve/Reject area */}
                <Paper sx={{ p: 2, borderRadius: 2 }} variant="outlined">
                  <Typography fontWeight={900} sx={{ mb: 1 }}>
                    Review / Decision
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    label="Review reason (required if rejecting)"
                    value={reviewReason}
                    onChange={(e) => setReviewReason(e.target.value)}
                    disabled={!canReview || reviewSubmitting}
                  />

                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      justifyContent: { xs: 'stretch', sm: 'flex-end' },
                    }}
                  >
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleSubmitReview('APPROVED')}
                      disabled={!canReview || reviewSubmitting}
                      startIcon={reviewSubmitting ? <CircularProgress size={16} /> : <CheckCircle2 size={18} />}
                      sx={{ minWidth: 160 }}
                    >
                      Approve
                    </Button>

                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleSubmitReview('REJECTED')}
                      disabled={!canReview || reviewSubmitting}
                      startIcon={reviewSubmitting ? <CircularProgress size={16} /> : <XCircle size={18} />}
                      sx={{ minWidth: 160 }}
                    >
                      Reject
                    </Button>

                    {!canReview && (
                      <Chip size="small" label="This request is already reviewed" variant="outlined" sx={{ alignSelf: 'center' }} />
                    )}
                  </Box>

                  {canReview ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Approve/Reject only works while status is PENDING.
                    </Typography>
                  ) : null}
                </Paper>

                <Divider />

                {/* BEFORE / AFTER side-by-side */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <ReceiptCard
                    title="BEFORE"
                    tone="before"
                    meta="Original snapshot"
                    saleNorm={beforeNorm}
                    diff={diff}
                    rawFallback={selected.before_json}
                  />
                  <ReceiptCard
                    title="AFTER"
                    tone="after"
                    meta="Edited snapshot"
                    saleNorm={afterNorm}
                    diff={diff}
                    rawFallback={selected.after_json}
                  />
                </Box>

                {/* legenda */}
                <Box sx={{ mt: 1 }}>
                  <Chip size="small" label="Highlighted = changed" sx={{ bgcolor: 'rgba(255, 235, 59, 0.55)', fontWeight: 700 }} />
                </Box>
              </Box>
            )}
          </DialogContent>

          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Button onClick={closeDetails} variant="outlined" sx={{ minWidth: 140 }} startIcon={<X size={18} />} disabled={reviewSubmitting}>
              Close
            </Button>
          </Box>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
